import { z } from 'zod/v3';

import { marketECouponInfoSchema } from './e-coupon';

// ============================================================================
// Enum 스키마
// ============================================================================

/** 대표 배송비 산정방식 */
export const deliveryFeeTypeSchema = z.enum([
  'NONE',
  'FREE',
  'FIX',
  'CONDITIONALLY_FREE',
]);
export type DeliveryFeeType = z.infer<typeof deliveryFeeTypeSchema>;

/** 배송 플래그 */
export const deliveryFlagSchema = z.enum(['NONE', 'COURIER', 'SAFE_DELIVERY']);
export type DeliveryFlag = z.infer<typeof deliveryFlagSchema>;

/** 대표 배송형태 */
export const productDeliveryTypeSchema = z.enum([
  'NONE',
  'COURIER',
  'SELF',
  'E_COUPON',
  'INTANGIBLE_INSTANT',
  'INTANGIBLE_WITH_STATUS',
]);
export type ProductDeliveryType = z.infer<typeof productDeliveryTypeSchema>;

/** 넛징 타입 */
export const nudgeTypeSchema = z.enum([
  'STANDARD_MEMBERSHIP_DISCOUNT',
  'STANDARD_BONUS_PACK',
]);
export type NudgeType = z.infer<typeof nudgeTypeSchema>;

/** 가격 배지 */
export const priceBadgeSchema = z.enum([
  'DEFAULT',
  'MEMBERSHIP_WEEK',
  'LUCKY_BONUS_PACK',
]);
export type PriceBadge = z.infer<typeof priceBadgeSchema>;

/** 상품 타입 (실물, E쿠폰 등) */
export const marketProductTypeSchema = z.enum([
  'ACTUAL_PRODUCT',
  'ACTUAL_PRODUCT_1P',
  'E_COUPON',
  'ACTUAL_PRODUCT_1P_STOCK',
  'ADVERTISEMENT',
  'DATA_PRODUCT',
]);
export type MarketProductType = z.infer<typeof marketProductTypeSchema>;

/** 전용 상품 */
export const salesTargetSchema = z.enum([
  'NONE',
  'CASHNOTE_PLUS',
  'FIRST_ORDER',
  'VENDOR_FIRST_ORDER',
  'CASHNOTE_PLUS_100_DEAL',
  'FIRST_ORDER_PAY_LATER',
  'PAY_LATER',
]);
export type SalesTarget = z.infer<typeof salesTargetSchema>;

/** 상품 상태 */
export const productStatusSchema = z.enum([
  'READY_FOR_DISPLAY',
  'DISPLAYING',
  'DISPLAY_EXPIRED',
  'HIDED',
  'DISABLED_BY_ADMIN',
  'OUT_OF_STOCK',
  'NONEXISTENT',
]);
export type ProductStatus = z.infer<typeof productStatusSchema>;

/** 주문 가능 검증 코드 */
export const validCodeSchema = z.enum([
  'ORDERABLE',
  'READY_FOR_DISPLAY',
  'DISABLED',
  'OUT_OF_STOCK',
  'INVALID_REGION',
  'REQUIRED_PLUS_MEMBERSHIP',
  'REQUIRED_PLUS_MEMBERSHIP_FOR_UPGRADE',
  'NOT_PLUS_MEMBERSHIP',
  'NOT_FIRST_ORDER',
  'NOT_VENDOR_FIRST_ORDER',
]);
export type ValidCode = z.infer<typeof validCodeSchema>;

// ============================================================================
// 서브 스키마
// ============================================================================

/** 주문 가능 여부 */
export const validationResponseSchema = z.object({
  /** 유효 여부 */
  isValid: z.boolean(),
  /** 유효 여부 (deprecated alias) */
  valid: z.boolean().optional(),
  /** 검증 코드 */
  validCode: validCodeSchema,
  /** 검증 메시지 */
  validText: z.string(),
});
export type ValidationResponse = z.infer<typeof validationResponseSchema>;

// ============================================================================
// Model 스키마
// ============================================================================

/** 자주 구매한 상품 (MarketSelectProductResponseV2) */
export const frequentlyOrderProductSchema = z.object({
  advertisement: z.boolean().optional(),
  /** 캐시노트 플러스 적용시 판매단가 */
  cashnotePlusDiscountedPrice: z.number().optional(),
  /** 상품 분류 ID 리스트 */
  categoryIds: z.array(z.number()).optional(),
  /** 쿠폰 적용시 판매단가 */
  couponDiscountedPrice: z.number().optional(),
  /** 대표 배송비 */
  deliveryFee: z.number().optional(),
  /** 대표 배송비 산정방식 */
  deliveryFeeType: deliveryFeeTypeSchema.optional(),
  /** 배송 플래그 목록 */
  deliveryFlags: z.array(deliveryFlagSchema).optional(),
  /** 대표 배송형태 */
  deliveryType: productDeliveryTypeSchema.optional(),
  /** 할인률 */
  discountPercentage: z.number().optional(),
  /** 전시 분류 ID 리스트 */
  displayCategoryIds: z.array(z.number()).optional(),
  /** 디스플레이 랭크 */
  displayRank: z.number().optional(),
  /** E쿠폰 상품정보 */
  eCouponInfo: marketECouponInfoSchema.optional(),
  /** E쿠폰 주문버튼 비활성화 여부 */
  eCouponOrderDisabled: z.boolean(),
  /** E쿠폰 주문불가 라벨 */
  eCouponOrderDisabledLabel: z.string().optional(),
  freeShipping: z.boolean().optional(),
  hotDeal: z.boolean().optional(),
  /** 상품 광고 여부 */
  isAdvertisement: z.boolean().optional(),
  /** 무료배송 여부 */
  isFreeShipping: z.boolean(),
  /** 특가 여부 */
  isHotDeal: z.boolean(),
  isMembershipEventTarget: z.boolean().optional(),
  /** 품절여부 */
  isOutOfStock: z.boolean(),
  /** 대표이미지 */
  mainImages: z.array(z.string()).optional(),
  /** 입점몰명 */
  mallName: z.string().optional(),
  membershipEventTarget: z.boolean().optional(),
  /** 넛징 가격 */
  nudgeAmount: z.number().optional(),
  /** 넛징 적용 할인가 */
  nudgeTotalDiscountedPrice: z.number().optional(),
  /** 넛징 타입 */
  nudgeType: nudgeTypeSchema.optional(),
  /** 상품코드(OFFER) */
  offerCode: z.string().optional(),
  /** 상세페이지 주소 */
  offerDetailPageUrl: z.string().optional(),
  /** 상품ID(OFFER) */
  offerId: z.number().optional(),
  /** 구매 횟수 */
  orderCount: z.number().optional(),
  /** 주문 가능 여부 */
  orderable: validationResponseSchema,
  outOfStock: z.boolean().optional(),
  /** 판매단가 */
  price: z.number(),
  /** 가격 배지 */
  priceBadge: priceBadgeSchema.optional(),
  /** 할인전 단가 */
  priceBeforeDiscount: z.number().optional(),
  /** 상품명(OFFER) */
  productName: z.string(),
  /** 상품 타입 */
  productType: marketProductTypeSchema,
  /** 프로모션 텍스트 */
  promotionText: z.string().optional(),
  /** 전용 상품 */
  salesTargets: z.array(salesTargetSchema).optional(),
  /** 캐시노트 플러스 가입권유 노출 여부 */
  showCashnotePlusSuggestion: z.boolean().optional(),
  showcaseIds: z.array(z.number()),
  /** 품목 ID 리스트 */
  skuIds: z.array(z.number()),
  /** 상품 상태 */
  status: productStatusSchema.optional(),
  /** 총 할인 금액 */
  totalDiscountAmount: z.number().optional(),
  /** 총 할인 적용가 */
  totalDiscountedPrice: z.number().optional(),
  /** 입점몰 ID */
  vendorId: z.number(),
});
export type FrequentlyOrderProduct = z.infer<
  typeof frequentlyOrderProductSchema
>;
